import { supabase } from "./supabase";
import type { Episode } from "@/types/episode";
import { retryOperation } from "./utils";

// Default page size for initial loads
const DEFAULT_PAGE_SIZE = 50;

export const api = {
  episodes: {
    async getBookmarked(userId: string): Promise<Episode[]> {
      return retryOperation(async () => {
        // First get the bookmarks
        const { data: bookmarkData, error: bookmarkError } = await supabase
          .from("bookmarks")
          .select("episode_id")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(DEFAULT_PAGE_SIZE);

        if (bookmarkError) throw bookmarkError;
        if (!bookmarkData?.length) return [];

        // Then get the episode details
        const episodeIds = bookmarkData.map((b) => b.episode_id);
        const { data: episodesData, error: episodesError } = await supabase
          .from("episodes")
          .select(
            `
            id, 
            title, 
            description, 
            image_url, 
            duration, 
            audio_url, 
            published_at,
            podcast_id,
            podcasts!inner (
              id,
              title
            )
          `,
          )
          .in("id", episodeIds);

        if (episodesError) throw episodesError;

        // Combine the data
        return (episodesData || []).map((episode) => ({
          id: episode.id,
          title: episode.title,
          description: episode.description || undefined,
          imageUrl: episode.image_url || "",
          duration: episode.duration || "",
          audioUrl: episode.audio_url,
          podcastId: episode.podcasts?.id,
          podcastName: episode.podcasts?.title,
          publishedAt: new Date(episode.published_at),
          isBookmarked: true,
        }));
      });
    },

    async bookmark(userId: string, episodeId: string): Promise<void> {
      return retryOperation(async () => {
        const { error } = await supabase
          .from("bookmarks")
          .insert({ user_id: userId, episode_id: episodeId });
        if (error) throw error;
      });
    },

    async unbookmark(userId: string, episodeId: string): Promise<void> {
      return retryOperation(async () => {
        const { error } = await supabase
          .from("bookmarks")
          .delete()
          .eq("user_id", userId)
          .eq("episode_id", episodeId);
        if (error) throw error;
      });
    },

    async getLiked(userId: string): Promise<Episode[]> {
      return retryOperation(async () => {
        // First get the liked interactions
        const { data: likedData, error: likedError } = await supabase
          .from("liked_history")
          .select("episode_id")
          .eq("user_id", userId)
          .eq("interaction_type", "like")
          .order("created_at", { ascending: false })
          .limit(DEFAULT_PAGE_SIZE);

        if (likedError) throw likedError;
        if (!likedData?.length) return [];

        // Then get the episode details
        const episodeIds = likedData.map((l) => l.episode_id);
        const { data: episodesData, error: episodesError } = await supabase
          .from("episodes")
          .select(
            `
            id, 
            title, 
            description, 
            image_url, 
            duration, 
            audio_url, 
            published_at,
            podcast_id,
            podcasts!inner (
              id,
              title
            )
          `,
          )
          .in("id", episodeIds);

        if (episodesError) throw episodesError;

        // Fetch metrics for these episodes
        const { data: metricsData, error: metricsError } = await supabase
          .from("episode_metrics")
          .select("episode_id, listen_count, like_count")
          .in("episode_id", episodeIds);

        if (metricsError) throw metricsError;

        // Create a map of metrics for easy lookup
        const metricsMap = (metricsData || []).reduce(
          (acc, metric) => {
            acc[metric.episode_id] = metric;
            return acc;
          },
          {} as Record<string, { listen_count: number; like_count: number }>,
        );

        // Combine the data
        return (episodesData || []).map((episode) => ({
          id: episode.id,
          title: episode.title,
          description: episode.description || undefined,
          imageUrl: episode.image_url || "",
          duration: episode.duration || "",
          audioUrl: episode.audio_url,
          podcastId: episode.podcasts?.id,
          podcastName: episode.podcasts?.title,
          publishedAt: new Date(episode.published_at),
          isLiked: true,
          listens: metricsMap[episode.id]?.listen_count || 0,
          likes: metricsMap[episode.id]?.like_count || 0,
        }));
      });
    },

    async like(
      userId: string,
      episodeId: string,
      podcastId: string,
    ): Promise<void> {
      return retryOperation(async () => {
        const { error } = await supabase.from("liked_history").insert({
          user_id: userId,
          episode_id: episodeId,
          podcast_id: podcastId,
          interaction_type: "like",
          created_at: new Date().toISOString(),
        });
        if (error) throw error;
      });
    },

    async unlike(userId: string, episodeId: string): Promise<void> {
      return retryOperation(async () => {
        const { error } = await supabase
          .from("liked_history")
          .delete()
          .eq("user_id", userId)
          .eq("episode_id", episodeId)
          .eq("interaction_type", "like");
        if (error) throw error;
      });
    },

    async getDisliked(userId: string): Promise<Episode[]> {
      return retryOperation(async () => {
        // First get the disliked interactions
        const { data: dislikedData, error: dislikedError } = await supabase
          .from("liked_history")
          .select("episode_id")
          .eq("user_id", userId)
          .eq("interaction_type", "dislike")
          .order("created_at", { ascending: false })
          .limit(DEFAULT_PAGE_SIZE);

        if (dislikedError) throw dislikedError;
        if (!dislikedData?.length) return [];

        // Then get the episode details
        const episodeIds = dislikedData.map((d) => d.episode_id);
        const { data: episodesData, error: episodesError } = await supabase
          .from("episodes")
          .select(
            `
            id, 
            title, 
            description, 
            image_url, 
            duration, 
            audio_url, 
            published_at,
            podcast_id,
            podcasts!inner (
              id,
              title
            )
          `,
          )
          .in("id", episodeIds);

        if (episodesError) throw episodesError;

        // Combine the data
        return (episodesData || []).map((episode) => ({
          id: episode.id,
          title: episode.title,
          description: episode.description || undefined,
          imageUrl: episode.image_url || "",
          duration: episode.duration || "",
          audioUrl: episode.audio_url,
          podcastId: episode.podcasts?.id,
          podcastName: episode.podcasts?.title,
          publishedAt: new Date(episode.published_at),
          isDisliked: true,
        }));
      });
    },

    async dislike(
      userId: string,
      episodeId: string,
      podcastId: string,
    ): Promise<void> {
      return retryOperation(async () => {
        const { error } = await supabase.from("liked_history").insert({
          user_id: userId,
          episode_id: episodeId,
          podcast_id: podcastId,
          interaction_type: "dislike",
          created_at: new Date().toISOString(),
        });
        if (error) throw error;
      });
    },

    async undislike(userId: string, episodeId: string): Promise<void> {
      return retryOperation(async () => {
        const { error } = await supabase
          .from("liked_history")
          .delete()
          .eq("user_id", userId)
          .eq("episode_id", episodeId)
          .eq("interaction_type", "dislike");
        if (error) throw error;
      });
    },

    async recordListen(
      userId: string,
      episodeId: string,
      podcastId: string,
    ): Promise<void> {
      return retryOperation(async () => {
        console.log("Recording listen with data:", {
          userId,
          episodeId,
          podcastId,
          timestamp: new Date().toISOString()
        });
        
        const data = {
          user_id: userId,
          episode_id: episodeId,
          podcast_id: podcastId,
          progress: 0,
          completed: false,
          updated_at: new Date().toISOString(),
        };
        
        console.log("Attempting insert with data:", data);
        
        const { error: insertError } = await supabase
          .from("listen_history")
          .insert(data);

        if (insertError) {
          console.error("Error recording listen. Error details:", {
            code: insertError.code,
            message: insertError.message,
            details: insertError.details,
            hint: insertError.hint
          });
          throw insertError;
        }
        console.log("Successfully recorded listen");
      });
    },

    async getListenHistory(
      userId: string,
      page: number = 1,
      pageSize: number = DEFAULT_PAGE_SIZE,
    ): Promise<{ episodes: Episode[]; hasMore: boolean }> {
      return retryOperation(async () => {
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        // First get the listen history records
        const {
          data: historyData,
          error: historyError,
          count,
        } = await supabase
          .from("listen_history")
          .select("episode_id, progress, completed, updated_at, created_at", {
            count: "exact",
          })
          .eq("user_id", userId)
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false })
          .range(from, to);

        if (historyError) throw historyError;
        if (!historyData?.length) return { episodes: [], hasMore: false };

        // Then get the episode details
        const episodeIds = historyData.map((h) => h.episode_id);
        const { data: episodesData, error: episodesError } = await supabase
          .from("episodes")
          .select(
            `
            id, 
            title, 
            description, 
            image_url, 
            duration, 
            audio_url, 
            published_at,
            podcast_id,
            podcasts!inner (
              id,
              title
            )
          `,
          )
          .in("id", episodeIds);

        if (episodesError) throw episodesError;

        // Fetch metrics for these episodes
        const { data: metricsData, error: metricsError } = await supabase
          .from("episode_metrics")
          .select("episode_id, listen_count, like_count")
          .in("episode_id", episodeIds);

        if (metricsError) throw metricsError;

        // Create a map of metrics for easy lookup
        const metricsMap = (metricsData || []).reduce(
          (acc, metric) => {
            acc[metric.episode_id] = metric;
            return acc;
          },
          {} as Record<string, { listen_count: number; like_count: number }>,
        );

        // Combine the data
        const episodes = (episodesData || []).map((episode) => ({
          id: episode.id,
          title: episode.title,
          description: episode.description || undefined,
          imageUrl: episode.image_url || "",
          duration: episode.duration || "",
          audioUrl: episode.audio_url,
          podcastId: episode.podcasts?.id,
          podcastName: episode.podcasts?.title,
          publishedAt: new Date(episode.published_at),
          listens: metricsMap[episode.id]?.listen_count || 0,
          likes: metricsMap[episode.id]?.like_count || 0,
        }));

        return {
          episodes,
          hasMore: count ? count > to + 1 : false,
        };
      });
    },

    async updateProgress(
      userId: string,
      episodeId: string,
      podcastId: string,
      progress: number,
    ): Promise<void> {
      return retryOperation(async () => {
        // First, get any existing listen history entry
        const { data: existingData, error: existingError } = await supabase
          .from("listen_history")
          .select("*")
          .eq("user_id", userId)
          .eq("episode_id", episodeId)
          .maybeSingle();

        if (existingError) {
          throw existingError;
        }

        // Only update progress if a record already exists
        // This prevents creating new records before the 10-second listen timeout
        if (existingData) {
          const { error: updateError } = await supabase
            .from("listen_history")
            .update({
              progress,
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingData.id);

          if (updateError) throw updateError;
        }
        // Don't create a new record if one doesn't exist - wait for recordListen to do that
      });
    },
  },
};
